import { cn } from '@/lib/cn';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * 진행 entry — 두 종류:
 *  - dedupKey 가 있으면 같은 작업의 상태 전이 (예: 시트별 시작 → 완료). update 됨.
 *  - dedupKey 가 없으면 단순 로그 (progress / 일반 메시지). append 됨.
 */
export interface ProgressEntry {
  id: string;
  /** 같은 작업의 진행→완료를 한 entry 로 합치기 위한 키. 없으면 매번 새 entry. */
  dedupKey?: string;
  /** progress: 일반 안내, sheet_in_progress: 처리 중, *_done: 완료/오류. */
  kind: 'progress' | 'sheet_in_progress' | 'sheet_done' | 'ingest_done' | 'ingest_error' | 'error';
  text: string;
  /** 시작 시각 (epoch ms). dedupKey 작업의 경과 시간 계산용. */
  startedAt?: number;
  /** 완료 시각. dedupKey 작업이 done 상태일 때 표시. */
  endedAt?: number;
}

export function ExcelIngestProgressList({ entries }: { entries: ProgressEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-foreground-muted">진행 메시지를 기다리는 중…</p>;
  }
  return (
    <ul className="space-y-1.5">
      {entries.map((e) => (
        <li
          key={e.id}
          className={cn(
            'flex items-start gap-2 text-sm rounded-md px-3 py-1.5',
            e.kind === 'progress' && 'text-foreground-muted',
            e.kind === 'sheet_in_progress' && 'bg-accent/10 text-foreground',
            e.kind === 'sheet_done' && 'bg-surface-muted/40 text-foreground',
            e.kind === 'ingest_done' && 'bg-success/10 text-foreground',
            e.kind === 'ingest_error' && 'bg-destructive/10 text-foreground',
            e.kind === 'error' && 'bg-destructive/15 text-destructive',
          )}
        >
          <span className="mt-0.5">
            {e.kind === 'sheet_in_progress' ? (
              <Loader2 className="size-3.5 animate-spin text-accent" />
            ) : e.kind === 'sheet_done' || e.kind === 'ingest_done' ? (
              <CheckCircle2 className="size-3.5 text-success" />
            ) : e.kind === 'error' || e.kind === 'ingest_error' ? (
              <XCircle className="size-3.5 text-destructive" />
            ) : (
              <span className="inline-block size-1.5 rounded-full bg-foreground-muted/60 mt-1.5" />
            )}
          </span>
          <span className="flex-1 break-words">{e.text}</span>
          <Timing startedAt={e.startedAt} endedAt={e.endedAt} />
        </li>
      ))}
    </ul>
  );
}

function Timing({ startedAt, endedAt }: { startedAt?: number; endedAt?: number }) {
  if (!startedAt) return null;
  if (endedAt) {
    return (
      <span className="shrink-0 text-[10px] text-foreground-muted tabular-nums">
        {formatDuration(endedAt - startedAt)}
      </span>
    );
  }
  return <ElapsedTicker startedAt={startedAt} />;
}

function ElapsedTicker({ startedAt }: { startedAt: number }) {
  // 1 초마다 리렌더 — 진행 항목 수가 적어 부담 없음.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="shrink-0 text-[10px] text-foreground-muted tabular-nums">
      {formatDuration(Date.now() - startedAt)}
    </span>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}
